/** Continuous audio input only. Financial execution stays in the durable agent.
 *
 * A final original input item is handed to the host once, in commit order.
 * Deltas, model responses and function arguments never become owner requests.
 * The host must durably accept each input before treating it as submitted.
 */
import { VoiceSpeaker } from './voiceSpeaker';

const transcriptionUsage = (value) => {
  if (value?.type === 'duration' && typeof value.seconds === 'number' && Number.isFinite(value.seconds)
      && value.seconds >= 0 && value.seconds <= 3300) return { type: 'duration', seconds: value.seconds };
  if (value?.type === 'tokens') {
    const fields = Object.fromEntries(['input_tokens','output_tokens','total_tokens'].filter((key) => key in value).map((key) => [key,value[key]]));
    if (Object.keys(fields).length && Object.values(fields).every((count) => Number.isInteger(count) && count >= 0 && count <= 1e9)) return { type: 'tokens', ...fields };
  }
  return null; // Unsupported/missing usage is unknown, not zero or owner content.
};

export class RealtimeInput {
  constructor({ createSession, endSession, onInput, onSpeechStart = () => {}, onTranscriptionIssue = () => {},
    onState = () => {}, media = navigator.mediaDevices,
    Peer = window.RTCPeerConnection, documentRef = document,
    wakeLock = navigator.wakeLock, readyTimeoutMs = 10000,
    model = 'gpt-realtime-2.1', transcriptionModel = 'gpt-transcribe', orderTimeoutMs = 15000,
    output = false, initialMuted = false, onOutputEvent = () => false, onAudioState = () => {},
    speakerFactory = (options) => new VoiceSpeaker(options) }) {
    Object.assign(this, { createSession, endSession, onInput, onSpeechStart, onTranscriptionIssue,
      onState, media, Peer, documentRef, wakeLock, readyTimeoutMs, model, transcriptionModel, orderTimeoutMs,
      output, initialMuted, onOutputEvent, onAudioState, speakerFactory });
    this.run = null;
    this.generation = 0;
  }

  state(run, phase, detail = null) {
    if (this.run === run) {
      run.phase = phase;
      this.observe(() => this.onState({ phase, detail: detail || run.awakeProblem || null }));
    }
  }

  observe(action) {
    try { Promise.resolve(action()).catch(() => {}); } catch (_) { /* Optional observer/cleanup. */ }
  }

  start() {
    if (this.run) throw new Error('A voice connection is already active.');
    if (this.documentRef.visibilityState !== 'visible') {
      this.observe(() => this.onState({ phase: 'ended', detail: 'backgrounded' }));
      return Promise.resolve(false);
    }
    const run = { ended: false, ready: false, muted: Boolean(this.initialMuted), inputs: new Map(),
      startedAt: performance.now(),
      outputRequests: new Map(), outputResponses: new Map(), narrationAnchors: new Map(),
      delivered: new Set(), previous: null, previousAdmitted: null,
      tracks: [], session: null, generation: ++this.generation };
    this.run = run;
    run.stopped = new Promise((resolve) => { run.stopResolve = resolve; });
    run.timer = setTimeout(() => this.end('setup_timeout', run), this.readyTimeoutMs);
    this.state(run, 'connecting');
    if (this.output) {
      try {
        run.speaker = this.speakerFactory({ onState: (event) => {
          if (!run.ended && this.run === run) this.observe(() => this.onAudioState({ ...event, sessionId: run.session?.session_id }));
        } });
      } catch (_) { this.state(run, 'connecting', 'audio_output_unavailable'); }
    }
    this.requestWake(run);
    run.visibility = () => {
      if (this.documentRef.visibilityState !== 'visible') this.end('backgrounded', run);
    };
    try { this.documentRef.addEventListener('visibilitychange', run.visibility); }
    catch (_) { this.end('lifecycle_unavailable', run); return Promise.resolve(false); }
    return Promise.race([this.connect(run), run.stopped]);
  }

  async connect(run) {
    try {
      const stream = await this.media.getUserMedia({ audio: {
        echoCancellation: true, noiseSuppression: true, autoGainControl: true,
      } });
      run.tracks = stream.getTracks();
      if (run.ended) { run.tracks.forEach((track) => this.observe(() => track.stop())); return false; }
      run.tracks.forEach((track) => { track.enabled = false; });
      run.pc = new this.Peer();
      stream.getAudioTracks().forEach((track) => run.pc.addTrack(track, stream));
      run.channel = run.pc.createDataChannel('oai-events');
      run.channel.onopen = () => {
        if (run.ended || this.run !== run || run.configurationSent) return;
        try {
          // Request the exact settings explicitly and acknowledge them before
          // capture. session.created alone does not establish transcription.
          run.channel.send(JSON.stringify({ type: 'session.update', session: {
            type: 'realtime', output_modalities: [this.output ? 'audio' : 'text'], tools: [],
            audio: { input: { transcription: { model: this.transcriptionModel },
              noise_reduction: { type: 'near_field' }, turn_detection: {
                type: 'semantic_vad', eagerness: 'low', create_response: false, interrupt_response: false,
              } } },
          } }));
          run.configurationSent = true;
        } catch (_) { this.end('configuration_unavailable', run); }
      };
      const ready = new Promise((resolve, reject) => {
        run.resolve = resolve;
        run.reject = reject;
      });
      // Install a rejection observer before any awaited network/setup work.
      ready.catch(() => {});
      run.channel.onmessage = ({ data }) => {
        if (run.ended || this.run !== run) return;
        try { this.event(run, JSON.parse(data)); } catch (_) { this.end('invalid_input_stream', run); }
      };
      run.channel.onclose = () => { if (!run.ended) this.end('connection_lost', run); };
      run.pc.ontrack = (event) => {
        if (run.ended || this.run !== run || !run.speaker) return;
        try { run.speaker.attach(event.streams[0] || new MediaStream([event.track])); }
        catch (_) { this.observe(() => this.onAudioState({ kind: 'unavailable', sessionId: run.session?.session_id })); }
      };
      run.pc.onconnectionstatechange = () => {
        if (run.pc.connectionState === 'failed' && !run.ended) this.end('connection_lost', run);
        if (run.pc.connectionState === 'connected') clearTimeout(run.disconnectTimer);
        if (run.pc.connectionState === 'disconnected' && !run.ended) {
          clearTimeout(run.disconnectTimer);
          run.disconnectTimer = setTimeout(() => {
            if (run.pc.connectionState === 'disconnected') this.end('connection_lost', run);
          }, 7000);
        }
      };
      await run.pc.setLocalDescription(await run.pc.createOffer());
      if (run.ended) return false;
      const session = await this.createSession({ sdp: run.pc.localDescription.sdp });
      run.session = session;
      if (run.ended) { await this.closeProvider(run); return false; }
      await run.pc.setRemoteDescription({ type: 'answer', sdp: session.sdp });
      await ready;
      if (run.ended) return false;
      run.ready = true;
      clearTimeout(run.timer);
      run.tracks.forEach((track) => { track.enabled = !run.muted; });
      this.state(run, run.muted ? 'muted' : 'listening');
      return true;
    } catch (_) {
      if (!run.ended) await this.end('setup_failed', run);
      return false;
    }
  }

  requestWake(run) {
    if (!this.wakeLock?.request) {
      if (this.wakeLock === undefined) {
        run.awakeProblem = 'screen_awake_unavailable'; this.state(run, run.phase);
      }
      return;
    }
    Promise.resolve().then(() => this.wakeLock.request('screen')).then((lease) => {
      if (run.ended) this.observe(() => lease.release());
      else {
        run.wake = lease;
        lease.addEventListener?.('release', () => {
          if (!run.ended) { run.awakeProblem = 'screen_awake_released'; this.state(run, run.phase); }
        });
      }
    }).catch(() => { if (!run.ended) { run.awakeProblem = 'screen_awake_unavailable'; this.state(run, run.phase); } });
  }

  event(run, event) {
    if (event.type === 'session.updated') {
      const input = event.session?.audio?.input;
      const valid = event.session?.model === this.model
        && input?.transcription?.model === this.transcriptionModel
        && input?.turn_detection?.type === 'semantic_vad'
        && input?.turn_detection?.eagerness === 'low'
        && input?.turn_detection?.create_response === false
        && input?.turn_detection?.interrupt_response === false
        && event.session?.tools?.length === 0
        && (!this.output || event.session?.output_modalities?.includes('audio'));
      if (valid && run.configurationSent) run.resolve();
      else if (run.ready) this.end('unexpected_provider_configuration', run);
      return;
    }
    if (run.ready && this.output && (event.type.startsWith('response.')
        || event.type.startsWith('output_audio_buffer.') || event.type === 'error')) {
      if (this.onOutputEvent(event, run.session.session_id) === true) {
        this.rememberNarration(run, event); return;
      }
    }
    if (event.type === 'error' || event.type === 'response.created') {
      this.end('unexpected_provider_response', run);
      return;
    }
    if (!run.ready) return;
    if (event.type === 'input_audio_buffer.speech_started') {
      this.observe(() => this.onSpeechStart({ itemId: event.item_id }));
      return;
    }
    const committed = event.type === 'input_audio_buffer.committed';
    const completed = event.type === 'conversation.item.input_audio_transcription.completed';
    const failed = event.type === 'conversation.item.input_audio_transcription.failed';
    if (!committed && !completed && !failed) return;
    if (!event.item_id || (completed && event.content_index !== 0)) throw new Error('Missing input identity');
    if (run.narrationAnchors.has(event.item_id)) throw new Error('Generated audio cannot become owner speech');
    const item = run.inputs.get(event.item_id) || { id: event.item_id };
    if (committed) {
      if (item.committed && item.previous !== event.previous_item_id) throw new Error('Conflicting input order');
      if (event.previous_item_id === item.id || [...run.inputs.values()].some((other) => other.id !== item.id
          && other.committed && other.previous === event.previous_item_id)) throw new Error('Forked input stream');
      item.committed = true;
      item.previous = event.previous_item_id;
    } else {
      const transcript = completed ? String(event.transcript || '').trim() : '';
      if (item.final && item.transcript !== transcript) throw new Error('Conflicting final input');
      if (!item.final && completed && event.usage !== undefined) {
        item.transcriptionObservation = {
          ...((typeof event.event_id === 'string' && /^[A-Za-z0-9_-]{1,160}$/.test(event.event_id)) ? { provider_event_id: event.event_id } : {}),
          client_elapsed_ms: Math.max(0, Math.floor(performance.now()-run.startedAt)), usage: transcriptionUsage(event.usage) };
      }
      if (!item.final && !transcript) item.issue = {
        kind: failed ? 'transcription_failed' : 'empty_transcription',
        provider_error_code: failed && /^[A-Za-z0-9_-]{1,80}$/.test(event.error?.code || '') ? event.error.code : null,
        ...(typeof event.event_id === 'string' && /^[A-Za-z0-9_-]{1,160}$/.test(event.event_id) ? { provider_event_id: event.event_id } : {}),
        client_elapsed_ms: Math.max(0, Math.floor(performance.now()-run.startedAt)),
        usage: transcriptionUsage(event.usage),
      };
      item.final = true;
      item.transcript = transcript;
    }
    run.inputs.set(item.id, item);
    if (run.inputs.size > 512) throw new Error('Restart this long voice session');
    this.flush(run);
  }

  async flush(run) {
    if (run.dispatching) return;
    run.dispatching = true;
    clearTimeout(run.orderTimer);
    // Transcription completions may arrive out of order. The provider commit
    // chain, rather than response arrival time, defines owner speech order.
    try { while (!run.ended) {
      const next = [...run.inputs.values()].filter((candidate) => candidate.committed
        && !run.delivered.has(candidate.id) && (candidate.previous === run.previous
          || (run.narrationAnchors.has(candidate.previous) && run.narrationAnchors.get(candidate.previous) === run.previous)));
      if (next.length > 1) throw new Error('Forked input stream');
      if (!next.length || !next[0].final) break;
      const input = next[0];
      if (input.transcript) {
        await this.onInput({ itemId: input.id, transcript: input.transcript,
          sessionId: run.session.session_id, previousAdmittedItemId: run.previousAdmitted,
          sourcePreviousItemId: input.previous,
          ...(input.transcriptionObservation ? { transcriptionObservation: input.transcriptionObservation } : {}) });
        run.previousAdmitted = input.id;
      }
      run.delivered.add(input.id);
      run.previous = input.id;
      if (!input.transcript) {
        this.state(run, run.muted ? 'muted' : 'listening', 'speech_not_transcribed');
        this.observe(() => this.onTranscriptionIssue({ item_id: input.id, ...input.issue }));
      }
    } } catch (_) { await this.end('input_handoff_failed', run); }
    finally {
      run.dispatching = false;
      if (!run.ended && [...run.inputs.values()].some((input) => !run.delivered.has(input.id))) {
        run.orderTimer = setTimeout(() => this.end('transcription_order_timeout', run), this.orderTimeoutMs);
      }
    }
  }

  mute(value) {
    const run = this.run;
    if (!run || run.ended) return;
    run.muted = Boolean(value);
    run.tracks.forEach((track) => { track.enabled = run.ready && !run.muted; });
    if (run.ready) this.state(run, run.muted ? 'muted' : 'listening');
  }

  quiescent() {
    const run = this.run;
    return Boolean(run?.ready && !run.ended && !run.dispatching
      && [...run.inputs.values()].every((input) => run.delivered.has(input.id)));
  }

  sendOutput(event, sessionId) {
    const run = this.run;
    if (!this.output || !run?.ready || run.ended || run.session.session_id !== sessionId) return false;
    if (!['response.create', 'response.cancel', 'output_audio_buffer.clear'].includes(event.type)) return false;
    if (event.type === 'response.create' && (event.response?.conversation !== 'none'
        || event.response?.tools?.length !== 0 || event.response?.metadata?.session_id !== sessionId
        || typeof event.response?.metadata?.playback_id !== 'string' || run.outputRequests.size >= 512)) return false;
    if (event.type === 'response.cancel' && typeof event.response_id !== 'string') return false;
    const playbackId = event.response?.metadata?.playback_id;
    if (event.type === 'response.create') {
      if (run.outputRequests.has(playbackId)) return false;
      run.outputRequests.set(playbackId, { previous: run.previous });
    }
    try { run.channel.send(JSON.stringify(event)); return true; }
    catch (_) { if (event.type === 'response.create') run.outputRequests.delete(playbackId); return false; }
  }

  rememberNarration(run, event) {
    // Some provider commits name an out-of-band narrated item as predecessor.
    // Bridge only a response we requested and the reader authenticated. Its
    // generated content is never admitted as an owner request.
    if (event.type === 'response.created') {
      const request = run.outputRequests.get(event.response?.metadata?.playback_id);
      const prior = run.outputResponses.get(event.response?.id);
      if (!request || !event.response?.id || (prior && prior !== request)) throw new Error('Unknown narration ancestry');
      run.outputResponses.set(event.response.id, request);
    }
    if (event.type === 'response.output_item.added' || event.type === 'response.done') {
      const request = run.outputResponses.get(event.response_id || event.response?.id);
      if (!request) throw new Error('Unknown narration response');
      const items = event.item ? [event.item] : event.response.output || [];
      for (const item of items) {
        if (item.type !== 'message' || item.role !== 'assistant' || typeof item.id !== 'string'
            || run.inputs.has(item.id) || (run.narrationAnchors.has(item.id) && run.narrationAnchors.get(item.id) !== request.previous)) {
          throw new Error('Conflicting narration item');
        }
        if (run.narrationAnchors.size >= 512 && !run.narrationAnchors.has(item.id)) throw new Error('Restart this long voice session');
        run.narrationAnchors.set(item.id, request.previous);
      }
      this.flush(run);
    }
  }

  speakOutput(playbackId, sessionId) {
    const run = this.run;
    if (!run?.ready || run.ended || run.session.session_id !== sessionId) return Promise.resolve(false);
    return run.speaker?.speak(playbackId) || Promise.resolve(false);
  }

  stopOutput(sessionId) {
    if (this.run?.session?.session_id === sessionId) this.observe(() => this.run.speaker?.mute());
  }

  drainOutput(playbackId, sessionId) {
    if (this.run?.session?.session_id === sessionId) this.observe(() => this.run.speaker?.serverStopped(playbackId));
  }

  async closeProvider(run) {
    if (!run.session || run.providerClosing) return;
    run.providerClosing = true;
    try { await this.endSession(run.session); }
    catch (_) {
      if (this.generation === run.generation) this.observe(() => this.onState({
        ...run.endSnapshot, serverEnd: 'unconfirmed',
      }));
    }
  }

  async end(reason = 'ended', run = this.run) {
    if (!run || run.ended || this.run !== run) return;
    run.ended = true;
    clearTimeout(run.timer);
    clearTimeout(run.orderTimer);
    clearTimeout(run.disconnectTimer);
    run.reject?.(new Error('Voice connection ended.'));
    this.observe(() => this.documentRef.removeEventListener('visibilitychange', run.visibility));
    this.observe(() => run.channel?.close());
    this.observe(() => run.pc?.close());
    this.observe(() => run.speaker?.close());
    run.tracks.forEach((track) => this.observe(() => track.stop()));
    this.observe(() => run.wake?.release());
    const pending = [...run.inputs.values()].filter((input) => !run.delivered.has(input.id))
      .map(({ id, previous, transcript }) => ({ itemId: id, previousItemId: previous, transcript }));
    run.endSnapshot = { phase: 'ended', detail: reason,
      ...(pending.length ? { pendingInputs: pending } : {}) };
    if (this.run === run) this.observe(() => this.onState(run.endSnapshot));
    if (this.run === run) this.run = null;
    run.stopResolve(false);
    await this.closeProvider(run);
  }
}
